import type { Cue, Run, Scenario } from "@/types";

/**
 * How a run reads in the review: pure, so the page stays a page.
 *
 * A beat is a situation, a line from a script or a picture; a run is a written
 * scene or a live one typed during the lesson. The record keeps all of them
 * in the same shape (`Cue`, `Run`), and these say how each is shown.
 */

/** The id a live run records for its scene, with version 0. Nothing to look up. */
export const LIVE_SCENARIO_ID = "live";

/**
 * The two lessons with no scene at all: turns, pairs and a timer; a running
 * order and calls. Opened with one of these as the "scene", and never looked
 * up either.
 */
export const ROOM_SCENARIO_ID = "room";
export const SHOW_SCENARIO_ID = "show";

export type LessonKind = "room" | "show";

/** What a lesson was, when it was one of the two that have no scene. */
export function lessonKind(session: { scenarioId?: string } | undefined): LessonKind | undefined {
  if (session?.scenarioId === ROOM_SCENARIO_ID) return "room";
  if (session?.scenarioId === SHOW_SCENARIO_ID) return "show";
  return undefined;
}

/** The catalogue key naming that kind of lesson. */
export function lessonKindKey(kind: LessonKind): string {
  return `sessions.kind.${kind}`;
}

export function isLiveRun(run: Pick<Run, "live" | "scenarioId">): boolean {
  return run.live === true || run.scenarioId === LIVE_SCENARIO_ID;
}

/**
 * Whether a run is a rehearsal of a script rather than a scene of situations.
 *
 * Every beat a line, or all but one or two: a script pasted into Composer may
 * carry a stage direction as a situation beat, and that does not stop it
 * being a script. The same rule as the backend's, which decides what the
 * group's history counts.
 */
export function isScriptedRun(run: Pick<Run, "cues" | "live">): boolean {
  if (run.live) return false;
  if (run.cues.length === 0) return false;
  const lines = run.cues.filter((cue) => cue.kind === "line").length;
  return lines >= Math.max(1, run.cues.length - 2) && lines * 2 > run.cues.length;
}

/** `scenarioId@version` for every written scene the runs played, once each. */
export function scenesToLoad(runs: readonly Run[]): string[] {
  return [
    ...new Set(
      runs
        .filter((run) => !isLiveRun(run) && run.scenarioVersion >= 1)
        .map((run) => `${run.scenarioId}@${run.scenarioVersion}`),
    ),
  ];
}

/**
 * The name of the role a seat played.
 *
 * The run's own copy first — it is what the cards showed, and the only name a
 * live run has ("Player 2") — then the scene's version, then the id.
 */
export function roleName(run: Run, scene: Scenario | undefined, roleId: string): string {
  return (
    run.roles?.find((role) => role.id === roleId)?.name ??
    scene?.roles.find((role) => role.id === roleId)?.name ??
    roleId
  );
}

export type BeatView =
  | { kind: "situation"; text: string; feeling: string }
  | { kind: "line"; text: string; feeling: string }
  | { kind: "image"; path: string; caption: string; feeling: string };

/** Where a picture lives in Storage. Content-addressed; never a per-scene copy. */
export function imagePath(sha256: string): string {
  return `scene-images/${sha256}.jpg`;
}

export function beatView(cue: Cue): BeatView {
  const feeling = cue.emotion.trim();
  if (cue.kind === "image" && cue.image) {
    return { kind: "image", path: imagePath(cue.image.sha256), caption: cue.situation.trim(), feeling };
  }
  if (cue.kind === "line") return { kind: "line", text: cue.situation, feeling };
  return { kind: "situation", text: cue.situation, feeling };
}
